import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, Lock, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { t, formatEur } from "@/i18n/sk";
import { useCart } from "@/context/CartContext";
import { createCheckout, resolveImg } from "@/lib/api";

const FIELDS = [
  { key: "full_name", label: t.checkout.fullName, required: true, span: 2 },
  { key: "email", label: t.checkout.email, required: true, type: "email" },
  { key: "phone", label: t.checkout.phone, type: "tel" },
  { key: "address", label: t.checkout.address, required: true, span: 2 },
  { key: "city", label: t.checkout.city, required: true },
  { key: "zip", label: t.checkout.zip, required: true },
  { key: "country", label: t.checkout.country, required: true },
];

export default function Checkout() {
  const { items, validated, refreshValidation, loadingValidation } = useCart();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", address: "", city: "", zip: "", country: "Slovensko", note: "",
  });
  const [terms, setTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (items.length > 0) refreshValidation(items);
  }, [items, refreshValidation]);

  const subtotal = validated?.subtotal_eur ?? 0;
  const shipping = validated?.shipping_eur ?? 0;
  const total = validated?.total_eur ?? subtotal;

  const set = (k, v) => { setForm((p) => ({ ...p, [k]: v })); setErrors((e) => ({ ...e, [k]: false })); };

  const validate = () => {
    const errs = {};
    FIELDS.forEach((f) => { if (f.required && !form[f.key].trim()) errs[f.key] = true; });
    if (!terms) errs.terms = true;
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async () => {
    if (!validate()) { toast.error("Skontrolujte prosím povinné polia."); return; }
    setSubmitting(true);
    try {
      const res = await createCheckout({
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        customer: form,
        terms_accepted: terms,
        origin_url: window.location.origin,
      });
      window.location.href = res.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Platbu sa nepodarilo spustiť.");
      setSubmitting(false);
    }
  };

  const lines = useMemo(() => validated?.items?.filter((i) => i.quantity > 0) || [], [validated]);

  if (items.length === 0) {
    return (
      <div className="vault-bg">
        <section className="mx-auto flex max-w-2xl flex-col items-center gap-5 px-4 py-24 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full border border-white/8 bg-vault-card">
            <ShoppingBag className="h-7 w-7 text-slate-500" />
          </div>
          <h1 className="font-display text-2xl font-bold text-white">{t.checkout.empty}</h1>
          <Link to="/" data-testid="checkout-empty-back" className="btn-glow rounded-full bg-gradient-to-r from-violet-glow to-cyan-glow px-6 py-2.5 text-sm font-semibold text-white">
            {t.checkout.backToShop}
          </Link>
        </section>
      </div>
    );
  }

  return (
    <div className="vault-bg">
      <section className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-8">
        <span className="overline mb-2 block">{t.checkout.eyebrow}</span>
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">{t.checkout.title}</h1>
        <p className="mt-3 max-w-2xl text-slate-300">{t.checkout.intro}</p>

        <div className="mt-10 grid gap-8 lg:grid-cols-[1.4fr_1fr]">
          {/* Form */}
          <div data-testid="checkout-form" className="flex flex-col gap-8">
            <div className="rounded-2xl border border-white/8 bg-vault-card p-6">
              <h2 className="mb-5 font-display text-lg font-bold text-white">{t.checkout.delivery}</h2>
              <div className="grid grid-cols-2 gap-4">
                {FIELDS.map((f) => (
                  <div key={f.key} className={f.span === 2 ? "col-span-2" : "col-span-2 sm:col-span-1"}>
                    <label className="mb-1.5 block text-xs font-medium text-slate-300">
                      {f.label}{f.required && <span className="text-rose-400"> *</span>}
                    </label>
                    <input
                      data-testid={`checkout-${f.key}`}
                      type={f.type || "text"}
                      value={form[f.key]}
                      onChange={(e) => set(f.key, e.target.value)}
                      className={`w-full rounded-xl border bg-vault-black/40 px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none ${errors[f.key] ? "border-rose-400/60" : "border-white/10 focus:border-violet-glow"}`}
                    />
                  </div>
                ))}
                <div className="col-span-2">
                  <label className="mb-1.5 block text-xs font-medium text-slate-300">{t.checkout.note}</label>
                  <textarea
                    data-testid="checkout-note"
                    rows={3}
                    value={form.note}
                    onChange={(e) => set("note", e.target.value)}
                    className="w-full rounded-xl border border-white/10 bg-vault-black/40 px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-violet-glow focus:outline-none"
                  />
                </div>
              </div>
            </div>

            <label className={`flex cursor-pointer items-start gap-3 rounded-xl border px-4 py-3.5 ${errors.terms ? "border-rose-400/60 bg-rose-400/5" : "border-white/8 bg-vault-card"}`}>
              <input
                data-testid="checkout-terms"
                type="checkbox"
                checked={terms}
                onChange={(e) => { setTerms(e.target.checked); setErrors((x) => ({ ...x, terms: false })); }}
                className="mt-0.5 h-4 w-4 accent-violet-500"
              />
              <span className="text-sm text-slate-300">
                {t.checkout.terms}{" "}
                <Link to="/obchodne-podmienky" className="text-cyan-300 underline">{t.footer.policies.terms}</Link>
              </span>
            </label>
          </div>

          {/* Summary */}
          <div className="flex flex-col gap-4">
            <div className="rounded-2xl border border-white/8 bg-vault-card p-6">
              <h2 className="mb-4 font-display text-lg font-bold text-white">{t.checkout.summary}</h2>
              <ul className="flex flex-col gap-3">
                {lines.map((it) => (
                  <li key={it.product_id} data-testid={`checkout-line-${it.slug}`} className="flex gap-3">
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-white/8 bg-vault-black/50 p-1.5">
                      {it.image && <img src={resolveImg(it.image)} alt={it.name} className="h-full w-full object-contain" />}
                    </div>
                    <div className="flex flex-1 flex-col">
                      <span className="line-clamp-2 text-sm font-medium text-white">{it.name}</span>
                      <span className="text-xs text-slate-400">{it.quantity} × {formatEur(it.unit_price_eur)}</span>
                    </div>
                    <span className="font-mono text-sm font-semibold text-white">{formatEur(it.line_total_eur)}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-5 flex flex-col gap-1.5 border-t border-white/8 pt-4 text-sm">
                <div className="flex justify-between text-slate-300"><span>{t.cart.subtotal}</span><span className="font-mono">{formatEur(subtotal)}</span></div>
                <div className="flex justify-between text-slate-300"><span>{t.cart.shipping}</span><span className="font-mono">{shipping === 0 ? t.cart.freeShipping : formatEur(shipping)}</span></div>
                <div className="mt-1 flex justify-between border-t border-white/8 pt-2 text-base font-bold text-white">
                  <span>{t.cart.total}</span>
                  <span className="font-mono" data-testid="checkout-total">{loadingValidation ? "…" : formatEur(total)}</span>
                </div>
              </div>
              <button
                data-testid="checkout-pay-button"
                onClick={submit}
                disabled={submitting}
                className="btn-glow mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-violet-glow to-cyan-glow px-6 py-3.5 text-sm font-semibold text-white disabled:opacity-60"
              >
                {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> {t.checkout.paying}</> : <><Lock className="h-4 w-4" /> {t.checkout.pay}</>}
              </button>
              <div className="mt-3 flex items-center justify-center gap-1.5 text-xs text-slate-400">
                <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" /> {t.checkout.securePay}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
